import { useEffect, useState } from 'react';

import type { TSectionElement } from '@shared/dream-components';

import type { NodeContent, Page } from '~/interfaces/page';
import type { Publication } from '~/interfaces/publication';
import { getColors } from '~/utils/findAndInsertProprietaryNode/getColors';

const checkOverflow = () => {
  const innerHeightPx = window.innerHeight;
  const visualViewportHeightPx = window.visualViewport?.height ?? 0;
  const positiveHeightsPx = [innerHeightPx, visualViewportHeightPx].filter((heightPx) => heightPx > 0);

  if (positiveHeightsPx.length === 0) {
    return true;
  }

  // Short pages often fill exactly one screen, so an equal height must keep the overlay.
  return document.body.scrollHeight > Math.min(...positiveHeightsPx);
};

const useProprietaryFooter = (
  pageData: Page['viewable_page_version']['content'],
  webTheme: Publication['web_theme'],
) => {
  const [measuredOverflow, setMeasuredOverflow] = useState<boolean | null>(null);
  const hasMeasuredOverflow = measuredOverflow !== null;
  // Unmeasured pages stay in normal flow so the footer cannot cover content before layout.
  const isPageContentOverflowing = measuredOverflow ?? true;

  useEffect(() => {
    const syncOverflowState = () => setMeasuredOverflow(checkOverflow());

    // No mount-time read: the observer's initial callback runs after layout, so it measures without forcing a reflow.
    const observer = new ResizeObserver(syncOverflowState);
    observer.observe(document.body);

    // Viewport-only resizes do not fire the body observer; re-check at most once per frame.
    let resizeFrame: number | null = null;
    const scheduleOverflowCheck = () => {
      if (resizeFrame !== null) return;
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        syncOverflowState();
      });
    };
    window.addEventListener('resize', scheduleOverflowCheck);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', scheduleOverflowCheck);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleOverflowCheck);
      visualViewport?.removeEventListener('resize', scheduleOverflowCheck);
      if (resizeFrame !== null) {
        cancelAnimationFrame(resizeFrame);
      }
    };
  }, []);

  if (!pageData || !pageData.content) {
    return {
      lastSection: undefined,
      isPageContentOverflowing,
      hasMeasuredOverflow,
      pageHasOneSection: false,
    };
  }

  const docContent = pageData.content as NodeContent[];
  const pageHasOneSection =
    docContent.length === 1 &&
    docContent[0].type === 'section' &&
    (docContent[0] as TSectionElement).attrs.isViewportHeight === true;

  const lastSection = (pageHasOneSection ? docContent[0] : docContent[docContent.length - 1]) as TSectionElement;

  const { textColor } = getColors(lastSection, webTheme);

  return {
    isPageContentOverflowing,
    hasMeasuredOverflow,
    pageHasOneSection,
    backgroundColor: lastSection?.attrs?.backgroundColor,
    textColor,
  };
};

export default useProprietaryFooter;
